import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'
export type UiLanguage = 'en' | 'hi'

interface UiState {
  theme: Theme
  /**
   * Interface language. The durable, cross-device source of truth is the
   * server-side profile preference (`interfaceLanguage`, persisted through
   * Save changes on the Profile pane); this mirrors it per browser so a reload
   * does not flash English before that profile loads — the same reason `theme`
   * is persisted here.
   */
  uiLanguage: UiLanguage
  /** Desktop sidebar collapsed to icons only. Persisted per browser. */
  isSidebarCollapsed: boolean
  /** Mobile nav drawer. Never persisted — it must not be open on load. */
  isMobileNavOpen: boolean
  isCommandPaletteOpen: boolean
  setTheme: (theme: Theme) => void
  setUiLanguage: (uiLanguage: UiLanguage) => void
  toggleSidebar: () => void
  setMobileNavOpen: (open: boolean) => void
  setCommandPaletteOpen: (open: boolean) => void
}

/**
 * Persisted under 'sabha.ui'. index.html reads the same key before first paint
 * to avoid a light flash on a dark-mode reload — if you rename the key, rename
 * it there too.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: 'system',
      uiLanguage: 'en',
      isSidebarCollapsed: false,
      isMobileNavOpen: false,
      isCommandPaletteOpen: false,
      setTheme: (theme) => set({ theme }),
      setUiLanguage: (uiLanguage) => set({ uiLanguage }),
      toggleSidebar: () => set((s) => ({ isSidebarCollapsed: !s.isSidebarCollapsed })),
      setMobileNavOpen: (isMobileNavOpen) => set({ isMobileNavOpen }),
      setCommandPaletteOpen: (isCommandPaletteOpen) => set({ isCommandPaletteOpen }),
    }),
    {
      name: 'sabha.ui',
      partialize: (state) => ({
        theme: state.theme,
        uiLanguage: state.uiLanguage,
        isSidebarCollapsed: state.isSidebarCollapsed,
      }),
    },
  ),
)

/** Applies the resolved theme to <html data-theme>. Called once by Providers. */
export function applyTheme(theme: Theme) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const resolved = theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme
  document.documentElement.dataset.theme = resolved
}
